import { Ship, PlayCircle, UsersRound, BookOpen, Contact, Package, ClipboardList, Wrench, ListChecks, Wallet, Receipt, FileText, Image as ImageIcon } from "lucide-react";
import { PageHeader } from "./Dashboard";
import { Button } from "../../components/ui/button";
import { useTour } from "../../components/tour/TourContext";
import { useAuth } from "../../context/AuthContext";

const SENIOR_ROLES = ["owner", "manager", "captain"];

const TUTORIALS = [
  {
    id: "add-yacht",
    icon: Ship,
    title: "How to add a yacht",
    description:
      "A guided, interactive walkthrough of creating your first yacht — role selection, cover photo, basic details, engines, generators, tenders and stabilizers.",
    meta: "7 steps · about 1 minute",
    seniorOnly: true,
  },
  {
    id: "invite-crew",
    icon: UsersRound,
    title: "How to invite your crew",
    description:
      "Add people to your yacht — invite someone new by email or add an existing Seavanto user, and pick the right role in the chain of command.",
    meta: "5 steps · about 1 minute",
    seniorOnly: true,
  },
  {
    id: "logbook-entry",
    icon: BookOpen,
    title: "How to record a logbook entry",
    description:
      "Create a log entry with trip details, conditions and engine-hour readings that feed your periodic maintenance schedules.",
    meta: "5 steps · about 1 minute",
  },
  {
    id: "my-network",
    icon: Contact,
    title: "Your crew & supplier network",
    description:
      "Browse everyone you've worked with across all yachts, filter by role, and add offline suppliers to your personal address book.",
    meta: "7 steps · about 1 minute",
    seniorOnly: true,
  },
  {
    id: "add-inventory",
    icon: Package,
    title: "How to manage inventory",
    description:
      "Add spares and consumables — by barcode scan or by hand — set low-stock alerts and keep an audit trail of every adjustment.",
    meta: "5 steps · about 1 minute",
  },
  {
    id: "create-task",
    icon: ClipboardList,
    title: "How to create a task",
    description:
      "Log one-off jobs with system, priority and due date, assign them to crew or an external supplier, and follow the confirmation workflow.",
    meta: "5 steps · about 1 minute",
  },
  {
    id: "periodic-task",
    icon: Wrench,
    title: "How to schedule periodic tasks",
    description:
      "Set up recurring maintenance by calendar days or engine hours — Seavanto auto-creates the job each time it falls due.",
    meta: "6 steps · about 1 minute",
  },
  {
    id: "checklist",
    icon: ListChecks,
    title: "How to build checklists",
    description:
      "Seed the default checklists as a base, then build your own templates and run them live with your crew.",
    meta: "6 steps · about 1 minute",
  },
  {
    id: "petty-cash",
    icon: Wallet,
    title: "How petty cash works",
    description:
      "Appoint the cash custodian who holds the float, record top-ups and expenses with receipts, and follow the approval chain.",
    meta: "6 steps · about 1 minute",
  },
  {
    id: "invoices",
    icon: Receipt,
    title: "How to manage invoices",
    description:
      "Log supplier invoices with amounts, statuses and bank details, group them by supplier, and keep an eye on outstanding and overdue totals.",
    meta: "6 steps · about 1 minute",
  },
  {
    id: "documents",
    icon: FileText,
    title: "How to store documents",
    description:
      "Upload the yacht's paperwork — registration, insurance, certificates and manuals — categorise it and get warned before anything expires.",
    meta: "5 steps · about 1 minute",
  },
  {
    id: "media",
    icon: ImageIcon,
    title: "How to use Media",
    description:
      "Upload photos and videos, caption and tag them, then filter your gallery by tag to find condition shots and repair records fast.",
    meta: "5 steps · about 1 minute",
  },
];

export default function Help() {
  const { startTour } = useTour();
  const { user } = useAuth();
  const isSenior = SENIOR_ROLES.includes((user?.role || "").toLowerCase());

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader overline="Help" title="Tutorials" />
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tutorials-grid">
        {TUTORIALS.map((tut) => {
          const locked = tut.seniorOnly && !isSenior;
          return (
            <div
              key={tut.id}
              className="rounded-md border border-zinc-200 bg-white p-5 flex flex-col"
              data-testid={`tutorial-card-${tut.id}`}
            >
              <div className="inline-grid place-items-center h-10 w-10 rounded-md bg-zinc-50 border border-zinc-200">
                <tut.icon className="h-5 w-5 text-zinc-600" />
              </div>
              <div className="font-display text-lg font-medium mt-3 leading-tight">{tut.title}</div>
              <div className="text-[13px] text-zinc-600 mt-1.5 leading-snug flex-1">{tut.description}</div>
              <div className="text-[11px] text-zinc-400 mt-3">{tut.meta}</div>
              <div className="mt-4">
                <Button
                  className="bg-ink hover:bg-black"
                  size="sm"
                  disabled={locked}
                  onClick={() => startTour(tut.id)}
                  data-testid={`start-tutorial-${tut.id}`}
                >
                  <PlayCircle className="h-4 w-4 mr-2" /> Start tutorial
                </Button>
                {locked && (
                  <div className="text-[11px] text-zinc-500 mt-2">
                    Only owners, managers and captains can add yachts.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
